import { useState } from 'react';
import { Link } from 'react-router-dom';
import { changePassword } from '../../utils/apiAuth';
import { validateNewPassword, passwordErrorMessage } from '../../utils/passwordRules';
import { clearSession } from '../../utils/auth';
import OfflineHint, { useOnlineOnly } from '../OfflineHint';

// The server enforces 8; this form asks for 6, so a 6- or 7-character password
// passes here and comes back as a 422, which lands on the field below.
const MIN_LENGTH = 6;

const EMPTY = { oldPassword: '', newPassword: '', confirmation: '' };

function ChangePasswordForm() {
  const offline = useOnlineOnly();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [oldPasswordError, setOldPasswordError] = useState('');
  const [done, setDone] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.oldPassword) {
      setOldPasswordError('Enter your current password');
      return;
    }
    const invalid = validateNewPassword(form.newPassword, form.confirmation, MIN_LENGTH);
    if (invalid) {
      setFieldError(invalid);
      return;
    }

    setLoading(true);
    setError('');
    setFieldError('');
    setOldPasswordError('');

    try {
      await changePassword(form.oldPassword, form.newPassword, form.confirmation);
      setForm(EMPTY);
      // The password that this session was opened with is gone, so the session
      // goes with it. Ended here rather than on the button so it is over even
      // if the user closes the tab on the confirmation.
      clearSession();
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      const message = passwordErrorMessage(err, 'Could not change the password. Please try again.');
      // 400 is a wrong current password (or an account with none) — the bearer
      // was fine, so this is a field error, never a session failure.
      if (status === 400) setOldPasswordError(message);
      else if (status === 422) setFieldError(message);
      else setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="password-changed">
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-700 dark:text-green-300">
            Your password has been changed and you have been signed out everywhere on this device.
          </p>
        </div>
        <Link
          to="/login"
          className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-sm font-bold text-white hover:bg-primary/90 transition-colors"
        >
          Sign in again
        </Link>
      </div>
    );
  }

  const inputClass = (invalid) =>
    `w-full px-3 py-2 border rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
      invalid ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
    }`;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div>
        <label
          htmlFor="oldPassword"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Current password
        </label>
        <input
          type="password"
          id="oldPassword"
          name="oldPassword"
          value={form.oldPassword}
          onChange={update}
          disabled={loading}
          autoComplete="current-password"
          className={inputClass(oldPasswordError)}
        />
        {oldPasswordError && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{oldPasswordError}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="newPassword"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          New password
        </label>
        <input
          type="password"
          id="newPassword"
          name="newPassword"
          value={form.newPassword}
          onChange={update}
          disabled={loading}
          autoComplete="new-password"
          className={inputClass(false)}
        />
      </div>

      <div>
        <label
          htmlFor="confirmation"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Confirm new password
        </label>
        <input
          type="password"
          id="confirmation"
          name="confirmation"
          value={form.confirmation}
          onChange={update}
          disabled={loading}
          autoComplete="new-password"
          className={inputClass(fieldError)}
        />
        {fieldError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldError}</p>}
      </div>

      <button
        type="submit"
        disabled={loading || offline}
        className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-sm font-bold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Changing...' : 'Change password'}
      </button>
      <OfflineHint action="Changing your password" />
    </form>
  );
}

export default ChangePasswordForm;
